import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { HashRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { UserProvider } from '@/contexts/UserContext';
import { CompetitionProvider } from '@/contexts/CompetitionContext';
import { QueryProvider } from '@/providers/QueryProvider';
import { PasswordGate } from '@/components/auth/PasswordGate';
import { DebugFooter } from '@/components/debug/DebugFooter';
import { playBoxClient } from '@/lib/play-box-client';
import { useEffect } from 'react';
import Index from './pages/Index';
import NotFound from './pages/NotFound';
import EnvVars from './pages/EnvVars';

const AppContent = () => {
  const { isLoading, authError } = useAuth();

  // Show error message if authentication failed
  if (authError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-app-background">
        <div className="text-center max-w-md px-6">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-white text-xl font-bold mb-2">Authentication Failed</h2>
          <p className="text-gray-400 mb-4">{authError.message}</p>
          <p className="text-gray-500 text-sm">
            Please check your connection and try again. If the problem persists, contact support.
          </p>
        </div>
      </div>
    );
  }

  // Show loading spinner while authenticating
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-app-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-bet99-primary mx-auto mb-4"></div>
          <p className="text-white">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <UserProvider>
      <CompetitionProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <HashRouter>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/welcome" element={<Index />} />
              <Route path="/picks/:gameId" element={<Index />} />
              <Route path="/how-to-play" element={<Index />} />
              <Route path="/main" element={<Index />} />
              <Route path="/main/:tab" element={<Index />} />
              <Route path="/competition/:gameId" element={<Index />} />
              <Route path="/league/:leagueId" element={<Index />} />
              <Route path="/env-vars" element={<EnvVars />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </HashRouter>
          {import.meta.env.VITE_SHOW_DEBUG_FOOTER === '1' && <DebugFooter />}
        </TooltipProvider>
      </CompetitionProvider>
    </UserProvider>
  );
};

const App = () => {
  useEffect(() => {
    // Initialize play-box client (singleton, safe to call multiple times)
    playBoxClient.initialize();

    // Only send IFRAME_INITIALISED once
    // Check if we've already sent it by looking for a flag in sessionStorage
    const iframeReadySent = sessionStorage.getItem('playbox_iframe_ready_sent');
    if (!iframeReadySent) {
      playBoxClient.ready();
      sessionStorage.setItem('playbox_iframe_ready_sent', 'true');
      console.log('[App] IFRAME_INITIALISED sent to host');
    } else {
      console.log('[App] IFRAME_INITIALISED already sent, skipping');
    }
  }, []);

  return (
    <PasswordGate>
      <QueryProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </QueryProvider>
    </PasswordGate>
  );
};

export default App;
